import {
    getRegistrar,
    listRegistrars
} from './queries';
import {
    createRegistrar,
    deleteRegistrar,
    updateRegistrar
} from './mutations';

import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {Registrar} from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";

// FNP-1292 — the LIST RESULT CARRIES ITS nextToken.
//
// ⚠️ The token was in the schema all along (`listRegistrars(filter, limit,
// nextToken): RegistrarConnection`) and the client threw it away twice over:
// the GraphQL document did not select the field, and `fetchRecords` returned
// `.items` alone. Token pagination was therefore not "missing" from this
// module — it was structurally impossible, exactly the trap
// `docs/patterns/DOMAIN_NAMES_MODULE_REMEDIATION.md` describes. Both halves are
// fixed here; the table is downstream of that.
export type RegistrarsPage = {
    items: Registrar[],
    nextToken: string | null,
}

export type ListRegistrars = {
    listRegistrars: RegistrarsPage
}

export type GetRegistrar = {
    getRegistrar: Registrar
}

export type CreateRegistrar = {
    createRegistrar: Registrar
}

export type UpdateRegistrar = {
    updateRegistrar: Registrar
}

export type DeleteRegistrar = {
    deleteRegistrar: string
}

const client = generateClient();

// Page size is a SERVER concern under token pagination — the `limit` sent with
// the query, fixed by the module. Never a "rows per page" dropdown, which
// belongs to offset pagination and cannot be honoured here.
export const PAGE_SIZE = 50;


/**
 * One page of registrars.
 *
 * ⚠️ THE ERROR IS RE-THROWN, NOT SWALLOWED.
 *
 * The previous body caught, notified and returned `undefined`, which made a
 * failed query indistinguishable from a tenant with no registrars: React Query
 * saw a successful resolution, `isError` stayed false, and the table rendered
 * its empty state. "The API is down" and "you have no registrars" are two of
 * the three states §3 requires this module to tell apart, so the throw is what
 * makes the error Alert reachable at all. The notification still fires — the
 * failure is announced AND propagated.
 */
export const fetchRecords = async (
    filter: object = {},
    limit: number = PAGE_SIZE,
    nextToken: string | null = null,
): Promise<RegistrarsPage> => {

    try {
        const response = await client.graphql<GraphQLQuery<ListRegistrars>>({
                                                                                query: (listRegistrars),
                                                                                variables: {
                                                                                    filter: filter,
                                                                                    limit: limit,
                                                                                    nextToken: nextToken,
                                                                                },
                                                                            });
        return {
            items: response.data?.listRegistrars?.items ?? [],
            // ⚠️ NORMALISED TO null. AppSync returns the field absent rather
            // than null at the end of the results, and `undefined` reaching
            // `getNextPageParam` reads the same as a token would not.
            nextToken: response.data?.listRegistrars?.nextToken ?? null,
        };
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
        throw e;
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetRegistrar>>({
                                                                              query: (getRegistrar),
                                                                              variables: {id: id},
                                                                          });
        return response.data?.getRegistrar;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const add = async (payload: object) => {
    try {
        const response = await client.graphql<GraphQLQuery<CreateRegistrar>>({
                                                                                 query: (createRegistrar),
                                                                                 variables: {input: payload},
                                                                             });

        showSuccessNotification();
        return response.data?.createRegistrar;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const update = async (id: string, record: Registrar) => {

    console.debug('IN UPDATE REGISTRAR', id, record);

    delete record.created;
    delete record.updated;

    try {
        const response = await client.graphql<GraphQLQuery<UpdateRegistrar>>({
                                                                                 query: (updateRegistrar),
                                                                                 variables: {input: record},
                                                                             });

        showSuccessNotification();
        return response.data?.updateRegistrar;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteRegistrar>>({
                                                                                 query: (deleteRegistrar),
                                                                                 variables: {input: {id: id}},
                                                                             });
        showSuccessNotification();
        return response.data?.deleteRegistrar;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};
